import {createSharedComposable, useEventListener} from '@vueuse/core';

export interface CookiebotConsentState {
	necessary: boolean;
	preferences: boolean;
	statistics: boolean;
	marketing: boolean;
	method: 'implied' | 'explicit' | null;
}

declare global {
	interface Window {
		Cookiebot?: {
			consent: CookiebotConsentState;
			consented: boolean;
			declined: boolean;
			hasResponse: boolean;
			doNotTrack: boolean;
			renew(): void;
			withdraw(): void;
			show(): void;
			hide(): void;
		};
	}
}

const DEFAULT_CONSENT: CookiebotConsentState = {
	necessary: true,
	preferences: false,
	statistics: false,
	marketing: false,
	method: null,
};

function useCookieConsentImpl() {
	const consent = ref<CookiebotConsentState>({...DEFAULT_CONSENT});
	const hasResponse = ref(false);

	function sync() {
		if (typeof window === 'undefined' || !window.Cookiebot) {
			return;
		}
		consent.value = {...window.Cookiebot.consent};
		hasResponse.value = window.Cookiebot.hasResponse;
	}

	if (import.meta.client) {
		sync();
		useEventListener(window, 'CookiebotOnConsentReady', sync);
		useEventListener(window, 'CookiebotOnAccept', sync);
		useEventListener(window, 'CookiebotOnDecline', sync);
	}

	return {
		consent: readonly(consent),
		hasResponse: readonly(hasResponse),
		marketingAllowed: computed(() => consent.value.marketing),
		statisticsAllowed: computed(() => consent.value.statistics),
		preferencesAllowed: computed(() => consent.value.preferences),
	};
}

// Share a single reactive state and one set of window listeners across the app on the client.
// Keep per-call instances on the server to avoid cross-request state leaking during SSR.
const useCookieConsent = import.meta.client
	? createSharedComposable(useCookieConsentImpl)
	: useCookieConsentImpl;

export default useCookieConsent;
